import { useQuery } from '@apollo/client';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';

import GetProductOwnership from 'bundles/entitlements-common/queries/GetProductOwnershipQuery.graphql';
import type {
  GetProductOwnershipQuery,
  GetProductOwnershipQueryVariables,
} from 'bundles/entitlements-common/queries/__generated__/GetProductOwnershipQuery';
import { formatGraphqlProductOwnership } from 'bundles/entitlements-common/utils/entitlementProductOwnershipUtils';
import type { FormattedGraphqlProductOwnership } from 'bundles/entitlements-common/utils/entitlementProductOwnershipUtils';
import { SPECIALIZATION, VERIFIED_CERTIFICATE } from 'bundles/payments-common/common/ProductType';

export type ProductOwnershipsReturnType = {
  loading?: boolean;
  courseOwnership?: FormattedGraphqlProductOwnership;
  s12nOwnership?: FormattedGraphqlProductOwnership;
};

/**
 * Hook to fetch product ownership data for a given product ownership ID.
 *
 * @param productOwnershipsId - The product ownership ID to fetch data for
 * @returns Object containing formatted product ownership data and loading state
 */
export const useGetProductOwnerships = (productOwnershipsId: string | undefined) => {
  const { data, loading } = useQuery<GetProductOwnershipQuery, GetProductOwnershipQueryVariables>(GetProductOwnership, {
    skip: !productOwnershipsId,
    variables: {
      productOwnershipId: productOwnershipsId || '',
    },
    context: { clientName: 'gatewayGql' },
  });

  return {
    productOwnership: formatGraphqlProductOwnership(data?.Entitlement?.queryProductOwnership),
    loading,
  };
};

/**
 * Custom hook to replace `withProductOwnerships` HOC.
 * Fetches ownership data for both course and specialization products.
 *
 * @param params - Object containing courseId and/or s12nId
 * @param params.courseId - Optional course ID to check ownership for
 * @param params.s12nId - Optional specialization ID to check ownership for
 * @returns Object containing course and specialization ownership data with loading state
 */
const useProductOwnerships = ({
  courseId,
  s12nId,
}: {
  courseId?: string;
  s12nId?: string;
}): ProductOwnershipsReturnType => {
  const courseProductOwnershipsId =
    user.isAuthenticatedUser() && courseId
      ? tupleToStringKey([user.get().id, VERIFIED_CERTIFICATE, courseId])
      : undefined;
  const s12nProductOwnershipsId =
    user.isAuthenticatedUser() && s12nId ? tupleToStringKey([user.get().id, SPECIALIZATION, s12nId]) : undefined;

  const { productOwnership: courseOwnership, loading: loadingCourseOwnership } =
    useGetProductOwnerships(courseProductOwnershipsId);
  const { productOwnership: s12nOwnership, loading: loadingS12nOwnership } =
    useGetProductOwnerships(s12nProductOwnershipsId);

  return {
    loading: loadingCourseOwnership || loadingS12nOwnership,
    courseOwnership,
    s12nOwnership,
  };
};

export default useProductOwnerships;
